/**
 * liveQuoteStream.ts — MER Algo Footprints
 *
 * Stage 17 (2026-04-27): Live watchlist quotes.
 *
 * A small singleton store that polls /api/market-data/quotes/batch every
 * QUOTE_POLL_MS and exposes the latest mid/bid/ask for each watchlist symbol
 * to React components via a subscription hook.
 *
 * Design choices:
 *
 *   - SEPARATE from the main Zustand store. Live quotes are ephemeral; we
 *     don't want them in the persist layer or interleaved with bar/symbol
 *     state. Keeps the production critical path (symbolData.candles) clean.
 *
 *   - SINGLE poll loop globally. Many components can subscribe; only one
 *     network request fires per cycle regardless of subscriber count.
 *
 *   - VISIBILITY GATING. When document.hidden goes true, the loop pauses.
 *     Resumes immediately on visibility change. Saves bandwidth and CPU
 *     when the user is on another tab.
 *
 *   - DATA-AVAILABILITY GATING. Calls isMarketDataAvailable() each cycle
 *     and skips fetch when markets are closed. No traffic on weekends.
 *
 *   - GRACEFUL FAILURE. Network errors logged once per cycle, no UI alarm.
 *     Stale data simply persists until next successful fetch.
 */

import { useEffect, useState } from 'react';
import { isMarketDataAvailable } from './useMarketData';

// ── Public types ──────────────────────────────────────────────────────────────

export interface LiveQuote {
  bid:        number | null;
  ask:        number | null;
  mid:        number | null;
  last:       number | null;
  bidSize:    number | null;
  askSize:    number | null;
  timestamp:  number | null;
  receivedAt: number;          // wall-clock when our hook stored it
}

// ── Internal state ────────────────────────────────────────────────────────────

const QUOTE_POLL_MS = 5_000;   // 5 seconds — agreed cadence
const MAX_BATCH     = 25;      // server caps at 25 too

let _state: Record<string, LiveQuote> = {};
let _subscribers: Array<() => void> = [];

let _watchedSymbols: string[] = [];
let _pollTimer: ReturnType<typeof setInterval> | null = null;
let _stopped       = false;
let _inFlight      = false;
let _visibilityHandlerInstalled = false;

// ── Internal helpers ──────────────────────────────────────────────────────────

function _notify() {
  for (const cb of _subscribers) {
    try { cb(); } catch { /* one bad subscriber shouldn't kill the rest */ }
  }
}

async function _pollOnce() {
  if (_inFlight) return;                        // overlap guard
  if (_stopped) return;
  if (typeof document !== 'undefined' && document.hidden) return;
  if (!isMarketDataAvailable()) return;
  if (_watchedSymbols.length === 0) return;

  const symbols = _watchedSymbols.slice(0, MAX_BATCH);
  const url = `/api/market-data/quotes/batch?symbols=${encodeURIComponent(symbols.join(','))}`;

  _inFlight = true;
  try {
    const r = await fetch(url, { credentials: 'include' });
    if (!r.ok) return;                          // log? not yet — every cycle would spam
    const data = await r.json();
    if (!data || !data.ok || !data.quotes) return;

    const now      = Date.now();
    const next     = { ..._state };
    let changed    = false;

    for (const sym of Object.keys(data.quotes)) {
      const q = data.quotes[sym];
      next[sym] = {
        bid:        q.bid       ?? null,
        ask:        q.ask       ?? null,
        mid:        q.mid       ?? null,
        last:       q.last      ?? null,
        bidSize:    q.bidSize   ?? null,
        askSize:    q.askSize   ?? null,
        timestamp:  q.timestamp ?? null,
        receivedAt: now,
      };
      changed = true;
    }

    if (changed) {
      _state = next;
      _notify();
    }
  } catch {
    // Silent. Next cycle will retry.
  } finally {
    _inFlight = false;
  }
}

function _ensureVisibilityHandler() {
  if (_visibilityHandlerInstalled) return;
  if (typeof document === 'undefined') return;
  _visibilityHandlerInstalled = true;
  document.addEventListener('visibilitychange', () => {
    // When the tab returns to foreground, kick off an immediate poll
    // so the user sees fresh prices instead of waiting up to QUOTE_POLL_MS.
    if (!document.hidden) void _pollOnce();
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Configure which symbols the stream should poll. Callers update this whenever
 * the watchlist composition changes. The poll loop is started/stopped lazily.
 */
export function setWatchedSymbols(symbols: string[]): void {
  // Normalize: uppercase, dedupe, drop empties
  const normalized = Array.from(new Set(
    symbols.map(s => String(s || '').trim().toUpperCase()).filter(Boolean)
  ));
  _watchedSymbols = normalized;

  if (_watchedSymbols.length === 0) {
    if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
    return;
  }

  _ensureVisibilityHandler();

  if (!_pollTimer) {
    // Fire one immediately so the UI doesn't wait QUOTE_POLL_MS for first data
    void _pollOnce();
    _pollTimer = setInterval(() => { void _pollOnce(); }, QUOTE_POLL_MS);
  }
}

/**
 * Stop polling entirely. Call only on app teardown or when explicitly
 * disabling live quotes (no UI for that yet, but the door is here).
 */
export function stopLiveQuotes(): void {
  _stopped = true;
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
}

/**
 * Subscribe to changes. Returns an unsubscribe function.
 * Used internally by useLiveQuote() — most callers shouldn't use this directly.
 */
function _subscribe(cb: () => void): () => void {
  _subscribers.push(cb);
  return () => {
    _subscribers = _subscribers.filter(x => x !== cb);
  };
}

/**
 * React hook: returns the latest LiveQuote for a symbol, or null if unknown.
 * Re-renders the consuming component whenever the store updates.
 */
export function useLiveQuote(symbol: string | null | undefined): LiveQuote | null {
  const [snapshot, setSnapshot] = useState<LiveQuote | null>(() =>
    symbol ? (_state[symbol.toUpperCase()] || null) : null
  );

  useEffect(() => {
    if (!symbol) { setSnapshot(null); return; }
    const sym = symbol.toUpperCase();
    setSnapshot(_state[sym] || null);
    return _subscribe(() => {
      setSnapshot(_state[sym] || null);
    });
  }, [symbol]);

  return snapshot;
}

/**
 * Test/inspection helper — current full state. Not for hot-path UI use.
 */
export function _debugGetAllQuotes(): Record<string, LiveQuote> {
  return { ..._state };
}
